import {
    CREATE_DEFAULTS_CARDINALITY,
    LABEL_OBJECT_FIELD_TYPES,
    NON_DROPDOWN_FACET_DISPLAYS,
    SELECT_FIELD_TYPES
} from './constants';
import type {
    BuildCreateDefaultsParticipationParams,
    CreateDefaultsCardinality,
    CreateDefaultsFieldMetadata,
    CreateDefaultsParticipationMapping,
    FacetForCreateDefaults,
    GetFiltersByIdentifier,
    ViewContextFilterValues
} from './types';

/** True when a facet lets the user select values from a list (single or multi select). */
export function isDropdownFacet(facet: FacetForCreateDefaults | undefined): boolean {
    const display = String(facet?.display ?? '').trim();
    return Boolean(display) && !NON_DROPDOWN_FACET_DISPLAYS.has(display);
}

/** Case-insensitive lookup of the values a view is filtered by for one field name. */
export function findFilterValuesForField(
    filterValues: ViewContextFilterValues | undefined,
    field: string | undefined
): string[] {
    const target = field?.trim().toLowerCase();
    if (!filterValues || !target) {
        return [];
    }

    for (const [identifier, values] of Object.entries(filterValues)) {
        if (identifier.trim().toLowerCase() === target) {
            return values;
        }
    }

    return [];
}

export function normalizeFilterValues(filterValues: Array<string | null | undefined>): string[] {
    return filterValues
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter((value): value is string => Boolean(value));
}

export function resolveCreateDefaultsFromViewContext(
    getFiltersByIdentifier: GetFiltersByIdentifier,
    mappings: CreateDefaultsParticipationMapping[]
): Record<string, unknown> {
    const defaults: Record<string, unknown> = {};

    for (const { filterIdentifier, field, cardinality, mapFilterValues } of mappings) {
        const filterEntries = getFiltersByIdentifier(filterIdentifier) || [];
        const normalized = normalizeFilterValues(filterEntries.map((filter) => filter?.value));

        if (cardinality === CREATE_DEFAULTS_CARDINALITY.single) {
            if (normalized.length !== 1) {
                continue;
            }
            defaults[field] = mapFilterValues ? mapFilterValues(normalized) : normalized[0];
            continue;
        }

        if (normalized.length === 0) {
            continue;
        }

        defaults[field] = mapFilterValues ? mapFilterValues(normalized) : normalized;
    }

    return defaults;
}

/**
 * Snapshots the dropdown filters the current view is narrowed by.
 *
 * Only select-style facets are collected, so immutable / system filters (entity, state, ...)
 * and non-selectable facets (search text, date or numeric ranges) can never reach a create surface.
 */
export function collectViewContextFilterValues(
    getFiltersByIdentifier: GetFiltersByIdentifier,
    facets: FacetForCreateDefaults[] | undefined
): ViewContextFilterValues {
    const filterValues: ViewContextFilterValues = {};

    for (const facet of facets || []) {
        const identifier = facet.identifier?.trim();
        if (!identifier || filterValues[identifier] || !isDropdownFacet(facet)) {
            continue;
        }

        const values = normalizeFilterValues((getFiltersByIdentifier(identifier) || []).map((filter) => filter?.value));
        if (values.length > 0) {
            filterValues[identifier] = values;
        }
    }

    return filterValues;
}

/** A create default may fill a field only while the user has not put anything in it. */
export function isUnsetValue(value: unknown): boolean {
    return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}

/** Applies resolved defaults onto a create draft without overwriting non-empty values. */
export function applyCreateDefaultsToRecord(
    record: Record<string, unknown>,
    defaults: Record<string, unknown> | undefined | null
): Record<string, unknown> {
    if (!defaults) {
        return record;
    }

    for (const [field, value] of Object.entries(defaults)) {
        if (value === undefined) {
            continue;
        }

        if (isUnsetValue(record[field])) {
            record[field] = value;
        }
    }

    return record;
}

function isSelectFieldType(fieldType: string | null | undefined): boolean {
    return SELECT_FIELD_TYPES.has(String(fieldType));
}

/**
 * Reference and location fields hold `{ label }` objects until a full reference is picked,
 * so filter strings must be wrapped. Plain and termset fields use the raw filter values.
 */
function resolveValueMapper(
    metadata: CreateDefaultsFieldMetadata | undefined,
    cardinality: CreateDefaultsCardinality
): ((values: string[]) => unknown) | undefined {
    if (!LABEL_OBJECT_FIELD_TYPES.has(String(metadata?.fieldType))) {
        return undefined;
    }

    if (cardinality === CREATE_DEFAULTS_CARDINALITY.multi) {
        return (values: string[]) => values.map((label) => ({ label }));
    }

    return (values: string[]) => ({ label: values[0] });
}

function toParticipationMapping(
    filterIdentifier: string,
    field: string,
    metadata: CreateDefaultsFieldMetadata | undefined
): CreateDefaultsParticipationMapping {
    const cardinality: CreateDefaultsCardinality = metadata?.isMultiSelect
        ? CREATE_DEFAULTS_CARDINALITY.multi
        : CREATE_DEFAULTS_CARDINALITY.single;
    const mapFilterValues = resolveValueMapper(metadata, cardinality);

    return {
        filterIdentifier,
        field,
        cardinality,
        ...(mapFilterValues ? { mapFilterValues } : {})
    };
}

/**
 * Builds participation for a view: facet-backed create targets plus custom select attributes.
 *
 * A filter identifier is matched to a create target by name, ignoring case, so that backend
 * facet ids such as `productiontype` still reach a `productionType` field.
 */
export function buildCreateDefaultsParticipation({
    fields,
    facets,
    fieldMetadata
}: BuildCreateDefaultsParticipationParams): CreateDefaultsParticipationMapping[] {
    const createTargetsByName = new Map<string, string>();
    for (const field of fields) {
        const target = field?.trim();
        if (target && !createTargetsByName.has(target.toLowerCase())) {
            createTargetsByName.set(target.toLowerCase(), target);
        }
    }

    const metadataByName = new Map<string, CreateDefaultsFieldMetadata>();
    for (const metadata of fieldMetadata || []) {
        const name = metadata.name?.trim();
        if (name) {
            metadataByName.set(name.toLowerCase(), metadata);
        }
    }

    const mappings = new Map<string, CreateDefaultsParticipationMapping>();
    const addMapping = (filterIdentifier: string, field: string) => {
        if (!mappings.has(field)) {
            mappings.set(
                field,
                toParticipationMapping(filterIdentifier, field, metadataByName.get(field.toLowerCase()))
            );
        }
    };

    for (const facet of facets || []) {
        const identifier = facet.identifier?.trim();
        if (!identifier || !isDropdownFacet(facet)) {
            continue;
        }

        const field = createTargetsByName.get(identifier.toLowerCase());
        if (field) {
            addMapping(identifier, field);
        }
    }

    // Custom select attributes are filterable by field name even when no facet is loaded yet.
    // Only create targets participate — same gate as facet-backed fields.
    for (const metadata of metadataByName.values()) {
        const name = metadata.name?.trim();
        if (!name || !isSelectFieldType(metadata.fieldType)) {
            continue;
        }

        const field = createTargetsByName.get(name.toLowerCase());
        if (field) {
            addMapping(name, field);
        }
    }

    return Array.from(mappings.values());
}
